<template>
    <form class="boeken" novalidate @submit.prevent>
        <div class="paneel">
            <!-- Step 1: Game -->
            <div class="stap">
                <div class="stap-kop" :class="{ klaar: step1 }">
                    <span class="nr">
                        <svg v-if="step1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                        <template v-else>1</template>
                    </span>
                    <h2>Game</h2>
                </div>
                <div class="twee-kol">
                    <div class="veld">
                        <label for="nb-cube">X-Cube</label>
                        <select id="nb-cube" v-model="booking.xcube_id" :disabled="ro('xcube_id')" @change="updateDevice">
                            <option :value="null">Choose an X-Cube</option>
                            <optgroup v-for="g in xcubeGroups" :key="g.name" :label="g.name">
                                <option v-for="x in g.xcubes" :key="x.id" :value="x.id">{{ x.name }}</option>
                            </optgroup>
                        </select>
                    </div>
                    <div class="veld">
                        <label for="nb-exp">Experience</label>
                        <select id="nb-exp" v-model="booking.experience_id" :disabled="!booking.xcube_id || ro('experience_id')" @change="updateOptions">
                            <option :value="null">{{ booking.xcube_id ? "Choose an experience" : "Choose an X-Cube first" }}</option>
                            <option v-for="x in experiences" :key="x.id" :value="x.id">{{ x.name }}</option>
                        </select>
                    </div>
                </div>
                <template v-if="booking.experience_id && experience.duration">
                    <div class="exp-info">
                        <span class="badge badge-stil">{{ experience.duration }} min</span>
                        <span v-if="playerOptions.length" class="badge badge-stil">{{ playerOptions[0] }}–{{ playerOptions[playerOptions.length - 1] }} players</span>
                    </div>
                    <div class="veld" style="margin: 0">
                        <label id="nb-sp-l">Number of players</label>
                        <div class="keuze" role="group" aria-labelledby="nb-sp-l">
                            <button v-for="n in playerOptions" :key="n" type="button" :disabled="ro('team_1_size')" :aria-pressed="booking.team_1_size === n" @click="setPlayers(n)">{{ n }}</button>
                        </div>
                    </div>
                </template>
            </div>

            <!-- Step 2: Customer -->
            <div class="stap">
                <div class="stap-kop" :class="{ klaar: step2 }">
                    <span class="nr">
                        <svg v-if="step2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                        <template v-else>2</template>
                    </span>
                    <h2>Customer</h2>
                </div>
                <div class="twee-kol">
                    <div class="veld"><label for="nb-naam">Name</label><input id="nb-naam" v-model="booking.name" :disabled="ro('name')" type="text" placeholder="Sanne de Vries" autocomplete="name" /></div>
                    <div class="veld"><label for="nb-email">Email</label><input id="nb-email" v-model="booking.email" :disabled="ro('email')" type="email" placeholder="sanne@example.com" autocomplete="email" /></div>
                    <div class="veld"><label for="nb-tel">Mobile (optional)</label><input id="nb-tel" v-model="booking.phone_number" :disabled="ro('phone_number')" type="tel" placeholder="+31 6 12345678" autocomplete="tel" /></div>
                    <div class="veld"><label for="nb-team">Team name</label><input id="nb-team" v-model="booking.team_1_name" :disabled="ro('team_1_name')" type="text" placeholder="Team De Vries" /></div>
                </div>
                <div v-if="languageOptions.length" class="veld" style="margin: 0">
                    <label id="nb-taal-l">Game language</label>
                    <div class="talen" role="group" aria-labelledby="nb-taal-l">
                        <button v-for="c in languageOptions" :key="c" type="button" :disabled="ro('language')" :aria-pressed="booking.language === c" @click="setLanguage(c)">
                            <span v-if="langInfo(c).flag" class="fi" :class="`fi-${langInfo(c).flag}`"></span>{{ langInfo(c).name }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Step 3: Date and time -->
            <div class="stap">
                <div class="stap-kop" :class="{ klaar: step3 }">
                    <span class="nr">
                        <svg v-if="step3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                        <template v-else>3</template>
                    </span>
                    <h2>Date and time</h2>
                </div>
                <div class="filter-rij">
                    <div class="veld" style="max-width: 260px"><label for="nb-datum">Date</label><input id="nb-datum" v-model="eventDateStr" :disabled="ro('eventDate')" type="date" :min="todayStr" /></div>
                    <div v-if="!ro('eventDate')" class="snel" style="padding-bottom: 2px">
                        <button v-for="d in quickDates" :key="d.value" class="chip" type="button" :aria-pressed="eventDateStr === d.value" @click="pickDate(d.value)">{{ d.label }}</button>
                    </div>
                </div>
                <p v-if="selectedDayOutside" class="hint">This day is outside the location's opening hours. You can still book it with a time below.</p>
                <template v-if="experience.duration">
                    <div v-if="slotsWithStatus.length" class="slots" role="group" aria-label="Start time">
                        <button v-for="s in slotsWithStatus" :key="s.time" type="button" :disabled="s.status === 'past' || s.status === 'unavailable'" :aria-pressed="s.status === 'selected'" @click="pickTime(s.time)">{{ s.time }}</button>
                    </div>
                    <div v-else class="leeg">
                        <h2>No free start times on this day</h2>
                        <p>Pick another date or a shorter experience, or enter a time below.</p>
                    </div>
                    <div v-if="slotsWithStatus.length" class="slot-legenda">
                        <span><i></i>Free</span>
                        <span><i class="bezet"></i>Taken or in the past</span>
                        <span><i class="gekozen"></i>Selected</span>
                        <span>Times are local to {{ (selectedLocation ? selectedLocation.name : "the location").split(" - ")[0] }}.</span>
                    </div>
                    <div class="veld" style="margin-top: var(--s-4); max-width: 260px">
                        <label for="nb-tijd">Or enter a time</label>
                        <input id="nb-tijd" v-model="eventTime" type="time" :disabled="ro('eventDate')" />
                    </div>
                    <p class="hint" style="margin-top: 6px">You can always enter a time, also outside opening hours.</p>
                    <p v-if="selectedTimeOutside" class="hint" style="color: var(--rood); font-weight: 600">This time is outside the opening hours. You can still book it.</p>
                </template>
                <p v-else class="hint" style="margin: 0">Choose an X-Cube and an experience first. Then the free start times for that day appear here.</p>
                <p v-if="eventDateTime && locationTimeZone !== userTimeZone" class="hint">
                    Local time: {{ dayjs(eventDateTime).format("DD-MM-YYYY HH:mm") }} {{ userTimeZone }}
                </p>
            </div>
        </div>

        <!-- Ticket summary -->
        <aside class="ticket" aria-label="Booking summary">
            <div class="kaart-wit">
                <div class="ticket-kop">
                    <span class="label">X-Cube booking</span>
                    <span class="titel">{{ experience.name || "New booking" }}</span>
                </div>
                <div class="ticket-body">
                    <div class="r"><span>Location</span><span :class="{ open: !selectedLocation }">{{ selectedLocation ? selectedLocation.name : "Not chosen yet" }}</span></div>
                    <div class="r"><span>X-Cube</span><span :class="xcube.name ? '' : 'open'">{{ xcube.name || "Not chosen yet" }}</span></div>
                    <div class="r"><span>Players</span><span :class="booking.team_1_size ? 'num' : 'open'">{{ booking.team_1_size || "Not chosen yet" }}</span></div>
                    <div class="r"><span>Date</span><span :class="{ open: !eventDateStr }">{{ eventDateStr ? dayjs(eventDate).format("ddd D MMM YYYY") : "Not chosen yet" }}</span></div>
                    <div class="r"><span>Start</span><span :class="eventTime ? 'num' : 'open'">{{ eventTime || "Not chosen yet" }}</span></div>
                    <div class="r"><span>Team</span><span :class="{ open: !booking.team_1_name }">{{ booking.team_1_name || "Not chosen yet" }}</span></div>
                    <div class="r">
                        <span>Language</span>
                        <span :class="{ open: !booking.language }">
                            <template v-if="booking.language"><span v-if="langInfo(booking.language).flag" class="fi" :class="`fi-${langInfo(booking.language).flag}`"></span>{{ langInfo(booking.language).name }}</template>
                            <template v-else>Not chosen yet</template>
                        </span>
                    </div>
                </div>
                <div class="perforatie"></div>
                <div class="ticket-voet"><slot></slot></div>
            </div>
        </aside>
    </form>
</template>

<script setup>
import { Message } from "element-ui";
import { computed, onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import * as Sentry from "@sentry/vue";
import { getLocationSchedule, getTimeSlots } from "../../api/rest";
import { calendarDateOf, dayIsOutsideOpeningHours, timeIsOutsideOpeningHours } from "../../helpers/openingHours";
import { geXCubesForLocations } from "../../services/xcubeService";
import { getExperiencesForDevice } from "../../services/experienceService";

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    value: {
        type: Object,
        required: true,
    },
    readonlyFields: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(["input"]);

const booking = computed({
    get: () => props.value,
    set: (val) => emit("input", val),
});

const eventDate = ref(booking.value.event_date ? dayjs(booking.value.event_date).toDate() : new Date());
const eventTime = ref(booking.value.event_time ? dayjs(booking.value.event_time).format("HH:mm") : "");
const experiences = ref([]);
const playerOptions = ref([]);
const languageOptions = ref([]);
const timeSlots = ref([]);
const schedule = ref(null);

const ro = (field) => props.readonlyFields.includes(field);

const slotsWithStatus = computed(() =>
    timeSlots.value.map((slot) => {
        let status = "available";
        const [hours, minutes] = slot.time.split(":").map(Number);
        const slotDateTime = dayjs(eventDate.value).tz(locationTimeZone.value, true).hour(hours).minute(minutes).second(0);
        if (slotDateTime.isBefore(dayjs())) {
            status = "past";
        } else if (slot.available === 0) {
            status = "unavailable";
        } else if (slot.time === eventTime.value) {
            status = "selected";
        }
        return { time: slot.time, status };
    }),
);

const updateDevice = async () => {
    const device = booking.value.xcube_id;
    booking.value.experience_id = null;
    booking.value.team_1_size = null;
    booking.value.language = null;
    booking.value.xcube_id = device;
    await updateOptions();
};

const experience = computed(() => experiences.value.find((exp) => exp.id === booking.value.experience_id) || {});

const xcube = computed(() => allXCubes.value.find((x) => x.id === booking.value.xcube_id) || {});

const updateOptions = async () => {
    experiences.value = await getExperiencesForDevice(booking.value.xcube_id);
    const po = [];
    if (booking.value.experience_id) {
        const controllers = xcube.value.config?.devices?.totalControllerCount || 8;
        const minPlayers = experience.value?.min_number_of_players || 2;
        const maxPlayers = Math.min(controllers, experience.value?.max_number_of_players || 8);
        if (experience.value) {
            for (let i = minPlayers; i <= maxPlayers; i++) {
                po.push(i);
            }
            if (!booking.value.team_1_size || booking.value.team_1_size < minPlayers) {
                booking.value.team_1_size = minPlayers;
            }
            if (booking.value.team_1_size > maxPlayers) {
                booking.value.team_1_size = maxPlayers;
            }
            languageOptions.value = experience.value.config?.availableLanguages || ["en"];
            if (!languageOptions.value.includes(booking.value.language)) {
                booking.value.language = languageOptions.value[0];
            }
        }

        let newTimeSlots = [];
        if (eventDate.value) {
            booking.value.event_date = eventDate.value;
            const { json, status } = await getTimeSlots(
                booking.value.xcube_id,
                dayjs(eventDate.value).format("YYYY-MM-DD"),
                experience.value.duration,
            );
            if (status !== 200) {
                Message.error({ message: `Error from server : ${json.message}` });
                return;
            }
            newTimeSlots = json;
        }
        timeSlots.value = newTimeSlots;
        booking.value.event_date = eventDate.value;
    }
    playerOptions.value = po;
};

const selectedLocation = computed(() =>
    props.locations.find((location) => location.xcubes.some((x) => x.id === booking.value.xcube_id)),
);

const locationTimeZone = computed(() => selectedLocation.value?.time_zone || userTimeZone.value);

const selectedDayOutside = computed(() => {
    if (!schedule.value || !eventDate.value || !selectedLocation.value?.time_zone) {
        return false;
    }
    return dayIsOutsideOpeningHours(schedule.value, calendarDateOf(eventDate.value), selectedLocation.value.time_zone);
});

const selectedTimeOutside = computed(() => {
    if (selectedDayOutside.value || !schedule.value || !eventTime.value || !selectedLocation.value?.time_zone) {
        return false;
    }
    return timeIsOutsideOpeningHours(schedule.value, {
        date: calendarDateOf(eventDate.value),
        time: eventTime.value,
        duration: experience.value.duration,
        timeZone: selectedLocation.value.time_zone,
    });
});

let scheduleRequest = 0;
const loadSchedule = async (locationId) => {
    const request = ++scheduleRequest;
    if (!locationId) {
        schedule.value = null;
        return;
    }
    try {
        const next = await getLocationSchedule(locationId);
        if (request === scheduleRequest) {
            schedule.value = next;
        }
    } catch (error) {
        if (request !== scheduleRequest) {
            return;
        }
        schedule.value = null;
        Message.error("Could not load opening hours");
        Sentry.captureException(error);
    }
};

watch(() => selectedLocation.value?.id, (locationId) => loadSchedule(locationId), { immediate: true });

const allXCubes = computed(() => geXCubesForLocations(props.locations));

const xcubeGroups = computed(() =>
    props.locations
        .map((loc) => ({ name: loc.name, xcubes: loc.xcubes.filter((x) => allXCubes.value.some((a) => a.id === x.id)) }))
        .filter((g) => g.xcubes.length),
);

const eventDateTime = computed(() => {
    if (eventDate.value && eventTime.value) {
        const [hours, minutes] = eventTime.value.split(":");
        return dayjs(eventDate.value).tz(locationTimeZone.value).hour(Number.parseInt(hours)).minute(Number.parseInt(minutes)).second(0).toDate();
    }
    return null;
});

const userTimeZone = computed(() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
        Sentry.captureException(error);
        return "Europe/Amsterdam";
    }
});

// Flag (flag-icons country code) + display name per game-language code.
// Flag codes differ from the language code where language != country
// (en->gb, da->dk, sv->se). Covers every availableLanguages value in the API.
const LANG_INFO = {
    nl: { name: "Nederlands", flag: "nl" },
    en: { name: "English", flag: "gb" },
    us: { name: "English (US)", flag: "us" },
    de: { name: "Deutsch", flag: "de" },
    da: { name: "Dansk", flag: "dk" },
    dk: { name: "Dansk", flag: "dk" },
    fr: { name: "Français", flag: "fr" },
    pt: { name: "Português", flag: "pt" },
    br: { name: "Português (BR)", flag: "br" },
    es: { name: "Español", flag: "es" },
    mx: { name: "Español (MX)", flag: "mx" },
    it: { name: "Italiano", flag: "it" },
    no: { name: "Norsk", flag: "no" },
    sv: { name: "Svenska", flag: "se" },
    lt: { name: "Lietuvių", flag: "lt" },
};
const langInfo = (code) => LANG_INFO[code] || { name: (code || "").toUpperCase(), flag: null };

const todayStr = dayjs().format("YYYY-MM-DD");

const eventDateStr = computed({
    get: () => (eventDate.value ? dayjs(eventDate.value).format("YYYY-MM-DD") : ""),
    set: (value) => {
        if (value && value >= todayStr) {
            eventDate.value = dayjs(value).toDate();
            updateOptions();
        }
    },
});

const quickDates = computed(() =>
    [0, 1, 2].map((offset) => {
        const d = dayjs().add(offset, "day");
        return { value: d.format("YYYY-MM-DD"), label: offset === 0 ? "Today" : offset === 1 ? "Tomorrow" : d.format("ddd D MMM") };
    }),
);

const pickDate = (value) => {
    eventDateStr.value = value;
};
const pickTime = (time) => {
    eventTime.value = time;
};
const setPlayers = (n) => {
    booking.value.team_1_size = n;
};
const setLanguage = (code) => {
    booking.value.language = code;
};

const step1 = computed(() => !!(booking.value.xcube_id && booking.value.experience_id && booking.value.team_1_size));
const step2 = computed(() => !!(booking.value.name && booking.value.email && booking.value.team_1_name));
const step3 = computed(() => !!eventTime.value);

onMounted(async () => {
    if (booking.value.experience_id) {
        await updateOptions();
    }
});

watch(eventDateTime, (newDateTime) => {
    booking.value.event_datetime = newDateTime;
});
</script>
